import React, { useState, useEffect } from 'react';
import type { Todo } from '@ext-bookmark/shared/types';
import type { Holiday } from '../services/todo/holidays';
import { getTodosForDate, toggleTodoComplete, getBaseTodoId } from '../services/todo/storage';
import { getRecurrenceTypeLabel } from '../services/todo/recurrence';
import { getLunarDisplay } from '@ext-bookmark/shared/utils';
import { ANIMATION_DURATION_MS, IOS_EASING } from '@ext-bookmark/shared/utils';

interface DateDetailDrawerProps {
  date: Date | null;
  holiday?: Holiday;
  isOpen: boolean;
  onClose: () => void;
  onTodoClick: (todo: Todo) => void;
  onAddTodo: (date: Date) => void;
  isMobile?: boolean;
}

const DateDetailDrawer: React.FC<DateDetailDrawerProps> = ({
  date,
  holiday,
  isOpen,
  onClose,
  onTodoClick,
  onAddTodo,
  isMobile: _isMobile = false,
}) => {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen && date) {
      loadTodos();
    }
  }, [isOpen, date]);

  // 控制抽屉的渲染时机，确保动画能正常播放
  useEffect(() => {
    if (isOpen) {
      // 打开时立即渲染，触发动画
      setShouldRender(true);
    } else {
      // 关闭时延迟移除，等待动画完成
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, ANIMATION_DURATION_MS); // 与动画时长一致
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const loadTodos = async () => {
    if (!date) return;
    const dateTodos = await getTodosForDate(date);
    setTodos(dateTodos);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const y = e.touches[0].clientY;
    setCurrentY(y);
    const deltaY = y - startY;
    if (deltaY > 0) {
      // 向下拖动
      e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    const deltaY = currentY - startY;
    if (deltaY > 100) {
      // 向下拖动超过100px，关闭抽屉
      onClose();
    }
    setIsDragging(false);
    setStartY(0);
    setCurrentY(0);
  };

  const weekdayNames = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  const weekday = date ? weekdayNames[date.getDay()] : '';
  const lunarDisplay = date ? getLunarDisplay(date) : '';
  
  // 计算抽屉的 translateY：拖拽时使用拖拽距离，否则根据 isOpen 状态决定位置
  // 使用百分比，100% 表示完全隐藏在屏幕下方
  const baseTranslateY = isOpen ? 0 : 100; // 0 = 显示，100 = 隐藏（100%）
  const dragTranslateY = isDragging && currentY > startY ? (currentY - startY) / window.innerHeight * 100 : 0;
  const translateY = baseTranslateY + dragTranslateY;
  
  // 计算缩放和透明度：拖拽时保持当前状态，否则根据 isOpen 状态决定
  const baseScale = isOpen ? 1 : 0.95;
  const baseOpacity = isOpen ? 1 : 0;
  // 拖拽时保持当前状态，不应用缩放和透明度变化
  const scale = isDragging ? 1 : baseScale;
  const opacity = isDragging ? 1 : baseOpacity;

  // 如果没有日期，不渲染
  if (!date) return null;
  
  // 如果不需要渲染且不在拖拽状态，不渲染
  if (!shouldRender && !isDragging) return null;

  return (
    <>
      {/* 背景遮罩 */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 pointer-events-none ${
          isOpen ? '' : 'pointer-events-none'
        }`}
        style={{
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: `opacity ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
        }}
        onClick={onClose}
      />

      {/* 抽屉 */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl z-50 max-h-[85vh] flex flex-col"
        style={{
          transform: `translateY(${translateY}%) scale(${scale})`,
          opacity: opacity,
          transition: isDragging 
            ? 'none' 
            : `transform ${ANIMATION_DURATION_MS}ms ${IOS_EASING}, opacity ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 拖拽指示器 */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>

        {/* 头部 */}
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="flex items-baseline gap-2 mb-1">
                <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {date.getDate()}
                </div>
                <div className="text-lg font-medium text-gray-500 dark:text-gray-400">
                  {weekday}
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                {date.toLocaleDateString('zh-CN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
                <span>农历 {lunarDisplay}</span>
                {holiday && (
                  <>
                    <span>·</span>
                    <span className={`px-2 py-0.5 rounded ${
                      holiday.isWorkday && holiday.isOffDay
                        ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
                        : holiday.isWorkday && !holiday.isOffDay
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                    }`}>
                      {holiday.name}
                    </span>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {todos.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                这一天没有待办事项
              </p>
              <button
                onClick={() => onAddTodo(date)}
                className="px-6 py-2.5 bg-ios-blue text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-shadow"
              >
                添加待办事项
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {todos.map((todo) => {
                const originalTodoId = getBaseTodoId(todo);
                const recurrenceLabel = todo.recurrence
                  ? getRecurrenceTypeLabel(todo.recurrence.type, todo.recurrence)
                  : null;
                const isRecurrenceInstance = !!todo.recurrence && todo.id !== originalTodoId;
                const instanceDate = isRecurrenceInstance ? todo.startDate : undefined;

                return (
                  <div
                    key={todo.id}
                    className="glass-card p-4 transition-all duration-200 cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={todo.completed}
                        onChange={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          // 先更新UI状态（乐观更新）
                          const newCompleted = !todo.completed;
                          setTodos(todos.map(t => 
                            t.id === todo.id ? { ...t, completed: newCompleted } : t
                          ));
                          
                          // 异步保存到存储，成功后仅局部更新，不整块重载
                          (async () => {
                            try {
                              const updated = await toggleTodoComplete(originalTodoId, instanceDate, {
                                currentInstanceCompleted: todo.completed,
                              });
                              if (updated) {
                                const normalizedInstance = instanceDate !== undefined
                                  ? new Date(instanceDate).setHours(0, 0, 0, 0)
                                  : undefined;
                                const completed = updated.recurrence && normalizedInstance !== undefined
                                  ? (updated.recurrence.completedDates || []).includes(normalizedInstance)
                                  : !!updated.completed;
                                setTodos(prev => prev.map(t => t.id !== todo.id ? t : {
                                  ...t,
                                  completed,
                                  recurrence: updated.recurrence ?? t.recurrence,
                                }));
                              }
                            } catch (error) {
                              console.error('[DateDetailDrawer] 切换待办事项状态失败:', error);
                              await loadTodos();
                            }
                          })();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                        }}
                        className="ios-checkbox mt-0.5"
                      />
                      <div 
                        className="flex-1 min-w-0"
                        onClick={(e) => {
                          // 点击任务内容区域编辑
                          const target = e.target as HTMLElement;
                          if (!target.closest('input[type="checkbox"]')) {
                            onTodoClick(todo);
                          }
                        }}
                        onTouchStart={(e) => {
                          // 长按检测（排除复选框）
                          const target = e.target as HTMLElement;
                          if (target.closest('input[type="checkbox"]')) {
                            return;
                          }
                          const timer = setTimeout(() => {
                            onTodoClick(todo);
                          }, 500);
                          const cleanup = () => {
                            clearTimeout(timer);
                            document.removeEventListener('touchend', cleanup);
                            document.removeEventListener('touchmove', cleanup);
                          };
                          document.addEventListener('touchend', cleanup);
                          document.addEventListener('touchmove', cleanup);
                        }}
                      >
                        <div
                          className={`text-base font-semibold mb-1.5 ${
                            todo.completed
                              ? 'line-through text-gray-400 dark:text-gray-500'
                              : 'text-gray-900 dark:text-gray-100'
                          }`}
                          style={{ color: todo.fontColor && !todo.completed ? todo.fontColor : undefined }}
                        >
                          {todo.title}
                        </div>
                        {todo.description && (
                          <div className={`text-sm text-gray-600 dark:text-gray-400 mb-2.5 leading-relaxed ${todo.completed ? 'line-through' : ''}`}>
                            {todo.description}
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          {recurrenceLabel && (
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">{recurrenceLabel}</span>
                          )}
                          {todo.reminder?.enabled && (
                            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">提醒</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 底部操作栏 - 只在有待办事项时显示 */}
        {todos.length > 0 && (
          <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <button
              onClick={() => onAddTodo(date)}
              className="w-full py-3 bg-ios-blue text-white rounded-xl font-medium text-base shadow-sm hover:shadow-md transition-shadow flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              添加待办事项
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default DateDetailDrawer;
